import React, { useState } from 'react';
import { BadgeCheckIcon, LockIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Meter } from '../../components/ui/Meter';
import { Tabs } from '../../components/ui/Tabs';
import { PipelineBoard } from '../../components/employer/PipelineBoard';
import { campaigns, employerEvents, employerListings, employerProfile, funnel, recruitmentMetrics, sourceUniversities } from '../../data/employer';
import { percent } from '../../utils/format';
import { useSectionRoute } from '../../hooks/useSectionRoute';
const TABS = ['Listings', 'Candidates', 'Campaigns', 'Events', 'Analytics', 'Company'] as const;
export function EmployerOverview() {
  const [tab, setTab] = useSectionRoute('/employer', TABS);
  const totalApplications = funnel[0].count;
  return <div className="space-y-6" data-id="element-8095" data-mp="i16t7venjygn">
      <PageHeader eyebrow="Employer" title={employerProfile.name} description={`${employerProfile.sector} · ${employerProfile.size} · ${employerProfile.locations.join(' · ')}`} actions={<Badge tone="ok" icon={<BadgeCheckIcon className="h-3.5 w-3.5" data-id="element-8098" data-mp="qzs7vfh06tc5" />} data-id="element-8097" data-mp="r367wh272qgf">
            Verified {employerProfile.verifiedOn}
          </Badge>} data-id="element-8096" data-mp="i1yb63fbtxog" />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-8099" data-mp="3uj3nj5j4kqm5">
        {recruitmentMetrics.map((metric, index) => <div key={metric.label} className={`rounded-card border bg-surface px-5 py-4 ${index === 0 ? 'border-brand-700' : 'border-line'}`} data-id="element-8100" data-mp="3dbzqcstlq2i" data-mpi={index}>
            <p className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-8101" data-mp="d2vizjkg3lxw">
              {metric.label}
            </p>
            <p className="cz-num mt-2 text-2xl font-semibold text-ink" data-id="element-8102" data-mp="3bq9hx27e6aul">{metric.value}</p>
            <p className="mt-1.5 text-[12px] leading-5 text-ink-muted" data-id="element-8103" data-mp="3gp3ydxhvznbj">{metric.note}</p>
          </div>)}
      </div>

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Employer sections" data-id="element-8104" data-mp="34ovugvhhgqee" />

      {tab === 'Listings' && <div className="space-y-3" data-id="element-8105" data-mp="3igty4qsfdu36">
          {employerListings.map(listing => <article key={listing.id} className="rounded-card border border-line bg-surface px-5 py-4" data-id="element-8106" data-mp="3dhhc289dor1n">
              <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-8107" data-mp="2prd6hvcmqh80">
                <div className="min-w-0" data-id="element-8108" data-mp="2ptg6973l3opx">
                  <div className="flex flex-wrap items-center gap-2" data-id="element-8109" data-mp="2lr356vzs8ezh">
                    <h2 className="text-[15px] font-semibold text-ink" data-id="element-8110" data-mp="2zpjbiltqg3h3">{listing.title}</h2>
                    <Badge data-id="element-8111" data-mp="2uqpvomqz40dr">{listing.kind}</Badge>
                    <Badge tone={listing.state === 'Published' ? 'ok' : listing.state === 'Draft' ? 'warn' : 'neutral'} data-id="element-8112" data-mp="2upyc6aanht3u">
                      {listing.state}
                    </Badge>
                  </div>
                  <p className="cz-num mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-ink-muted" data-id="element-8113" data-mp="1wyruogoqijfy">
                    <span data-id="element-8114" data-mp="1izicjbgyxf9c">
                      {listing.location} · {listing.workMode}
                    </span>
                    <span data-id="element-8115" data-mp="2bm352f96jxeh">{listing.compensationLabel}</span>
                    <span data-id="element-8116" data-mp="2bmmok0ehatum">{listing.deadlineLabel}</span>
                  </p>
                  <p className="mt-2 text-[12px] text-ink-subtle" data-id="element-8117" data-mp="2fmn45odbdb39">
                    Targets {listing.targetProgrammes.join(', ')} · {listing.targetLevels.join(', ')}
                  </p>
                  <ul className="mt-2 flex flex-wrap gap-1.5" data-id="element-8118" data-mp="26ul43r75pwti">
                    {listing.requirements.map(requirement => <li key={requirement} data-id="element-8119" data-mp="2uje1ls3fgcq8">
                        <Badge data-id="element-8120" data-mp="2zbfeng8f67hb">{requirement}</Badge>
                      </li>)}
                  </ul>
                </div>
                <div className="cz-num shrink-0 text-right text-[12px] text-ink-muted" data-id="element-8121" data-mp="1aqnvc8eo0fxz">
                  <p className="text-[15px] font-semibold text-ink" data-id="element-8122" data-mp="15pqeq09aztyl">{listing.applications}</p>
                  <p data-id="element-8123" data-mp="11q7p4r6jeupf">applications</p>
                  <p className="mt-1" data-id="element-8124" data-mp="11rah72flj36x">{listing.views.toLocaleString()} views</p>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3" data-id="element-8125" data-mp="1tgcihfc9ys2s">
                <Button size="sm" variant="secondary" onClick={() => setTab('Candidates')} data-id="element-8126" data-mp="1peqy78xevbik">
                  View candidates
                </Button>
                <Button size="sm" variant="ghost" data-id="element-8127" data-mp="1kmr3buw778su">
                  {listing.state === 'Draft' ? 'Publish' : 'Edit listing'}
                </Button>
                {listing.state === 'Draft' && <Badge tone="warn" icon={<LockIcon className="h-3.5 w-3.5" data-id="element-8129" data-mp="2d6fwr70tsw7y" />} data-id="element-8128" data-mp="2d54sz77drogu">
                    Posting fee billing gated
                  </Badge>}
              </div>
            </article>)}
        </div>}

      {tab === 'Candidates' && <PipelineBoard data-id="element-8130" data-mp="2kncfzztf7e8v" />}

      {tab === 'Campaigns' && <div className="space-y-3" data-id="element-8131" data-mp="2kpyoc1o4xxs5">
          {campaigns.map(campaign => <article key={campaign.id} className="rounded-card border border-line bg-surface px-5 py-4" data-id="element-8132" data-mp="2ple08o2l1jlx">
              <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-8133" data-mp="2tpvi2hpdtmu2">
                <div className="min-w-0" data-id="element-8134" data-mp="2ymu9gshnvfok">
                  <div className="flex flex-wrap items-center gap-2" data-id="element-8135" data-mp="33mqdgrqcz744">
                    <h2 className="text-[15px] font-semibold text-ink" data-id="element-8136" data-mp="33kvsjdf37f01">{campaign.name}</h2>
                    <Badge tone={campaign.state === 'Live' ? 'ok' : 'neutral'} data-id="element-8137" data-mp="38dymhmns0qgr">
                      {campaign.state}
                    </Badge>
                  </div>
                  <p className="cz-num mt-1.5 text-[13px] text-ink-muted" data-id="element-8138" data-mp="3hewjvm3plzqd">
                    {campaign.startsOn} — {campaign.endsOn} · {campaign.universities.join(' · ')}
                  </p>
                </div>
                <div className="w-full shrink-0 sm:w-56" data-id="element-8139" data-mp="1mbrqvb9xwz8b">
                  <Meter label="Shortlisted against target" value={percent(campaign.shortlisted, Math.max(campaign.targetHires * 3, 1))} trailing={`${campaign.shortlisted}/${campaign.targetHires * 3}`} data-id="element-8140" data-mp="1i9mysgz54ans" />
                  <p className="cz-num mt-2 text-[12px] text-ink-muted" data-id="element-8141" data-mp="1r88du25el7jt">
                    {campaign.applications} applications · {campaign.targetHires} hires targeted
                  </p>
                </div>
              </div>
            </article>)}
        </div>}

      {tab === 'Events' && <Panel title="Employer events" description="Published through the same events infrastructure students already use." bodyClassName="p-0" data-id="element-8142" data-mp="2a0stuj007i67">
          <ul className="divide-y divide-line" data-id="element-8143" data-mp="fmjgy2wlhjtc">
            {employerEvents.map(event => <li key={event.id} className="flex flex-wrap items-center gap-4 px-5 py-4" data-id="element-8144" data-mp="fow918w2hak4">
                <div className="min-w-0 flex-1" data-id="element-8145" data-mp="2rbyaodwhkjc5">
                  <p className="text-[13px] font-semibold text-ink" data-id="element-8146" data-mp="2w7wvjrilvj7t">{event.title}</p>
                  <p className="cz-num mt-0.5 text-[12px] text-ink-muted" data-id="element-8147" data-mp="2ii37vciu74zo">
                    {event.university} · {event.dateLabel} · {event.kind}
                  </p>
                </div>
                <div className="w-full sm:w-48" data-id="element-8148" data-mp="28jnhjbrmvzr8">
                  <Meter label="Registered" value={percent(event.registered, event.capacity)} trailing={`${event.registered}/${event.capacity}`} tone={percent(event.registered, event.capacity) > 90 ? 'warn' : 'brand'} data-id="element-8149" data-mp="28i2jvda0w0p5" />
                </div>
              </li>)}
          </ul>
        </Panel>}

      {tab === 'Analytics' && <div className="grid gap-6 lg:grid-cols-2" data-id="element-8150" data-mp="1snzed9u8bmd">
          <Panel title="Recruitment funnel" description="Conversion by stage this cycle." bodyClassName="p-0" data-id="element-8151" data-mp="1se8mngyy0fm">
            <ul className="divide-y divide-line" data-id="element-8152" data-mp="2lr1ggv4sa8it">
              {funnel.map(step => <li key={step.stage} className="px-5 py-3" data-id="element-8153" data-mp="2gqni64szicxz">
                  <Meter label={step.stage} value={percent(step.count, totalApplications)} trailing={`${step.count} · ${percent(step.count, totalApplications)}%`} tone={step.stage === 'Hired' ? 'ok' : 'brand'} data-id="element-8154" data-mp="2bydj9z9w62jk" />
                </li>)}
            </ul>
          </Panel>
          <Panel title="Source universities" bodyClassName="p-0" data-id="element-8155" data-mp="1t018ya78umme">
            <ul className="divide-y divide-line" data-id="element-8156" data-mp="1t2dqim93auyx">
              {sourceUniversities.map(source => <li key={source.name} className="px-5 py-3" data-id="element-8157" data-mp="1o3ashlrj1p0g">
                  <Meter label={source.name} value={percent(source.applications, sourceUniversities[0].applications)} trailing={`${source.applications}`} data-id="element-8158" data-mp="3nd0wqqls2n4r" />
                </li>)}
            </ul>
            <p className="border-t border-line px-5 py-3 text-[12px] leading-5 text-ink-muted" data-id="element-8159" data-mp="3ux1tj7dq9d90">
              Aggregate counts only. Individual student records are never exposed to employers
              through analytics.
            </p>
          </Panel>
        </div>}

      {tab === 'Company' && <div className="grid gap-6 lg:grid-cols-2" data-id="element-8160" data-mp="37yf8jscceiqn">
          <Panel title="Company profile" data-id="element-8161" data-mp="3byz812ke1yfy">
            <p className="text-[13px] leading-6 text-ink-muted" data-id="element-8162" data-mp="3c00ib4tkygwm">{employerProfile.about}</p>
            <dl className="mt-4 divide-y divide-line border-t border-line" data-id="element-8163" data-mp="3gxsj6oc7ddcy">
              {[{
            label: 'Sector',
            value: employerProfile.sector
          }, {
            label: 'Size',
            value: employerProfile.size
          }, {
            label: 'Locations',
            value: employerProfile.locations.join(', ')
          }, {
            label: 'Recruiter',
            value: employerProfile.recruiter
          }].map(row => <div key={row.label} className="flex flex-wrap justify-between gap-2 py-2.5" data-id="element-8164" data-mp="2ztgji6pn2ux5">
                  <dt className="text-[13px] text-ink-muted" data-id="element-8165" data-mp="34s1mgdw9zspa">{row.label}</dt>
                  <dd className="text-[13px] font-medium text-ink" data-id="element-8166" data-mp="39jt3nxtjkm2r">{row.value}</dd>
                </div>)}
            </dl>
          </Panel>
          <Panel title="Verification" data-id="element-8167" data-mp="3dnr206b1lsur">
            <p className="text-[13px] leading-6 text-ink-muted" data-id="element-8168" data-mp="2gvx8ixjmf283">
              Verified employers are checked by Campuzon against company registration and a named
              recruiter. Unverified listings remain visible to students but are labelled as
              unverified everywhere they appear, with a standing warning never to pay a fee to apply.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3" data-id="element-8169" data-mp="55v05kqi7cfb">
              <Badge tone="ok" icon={<BadgeCheckIcon className="h-3.5 w-3.5" data-id="element-8171" data-mp="a4emvncx8s8q" />} data-id="element-8170" data-mp="587i3xw9hmsd">
                Verified {employerProfile.verifiedOn}
              </Badge>
              <Badge tone="neutral" data-id="element-8172" data-mp="k7joto38rl7p">Re-verification due annually</Badge>
            </div>
          </Panel>
        </div>}
    </div>;
}